import {
	FormItem,
	ValidationError
} from "client/Components";
import { HubMediaSelect } from "client/Features/Hub/HubMediaSelect/HubMediaSelect";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { HubItemComposerDialog } from "../../HubItemComposerDialog/HubItemComposerDialog";
import { BuilderTitleInput } from "../../HubItemComposerDialog/BuilderTitleInput";
import { Builder, BuilderHandle } from "client/Features/Hub/Builder/Builder";
import { ExistingHubItemImages } from "client/Api/Hub/common.composer";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { EventFormModel } from "./EditEventDialog";
import { documentsConfiguration, WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { InputValidationResultKind } from "client/Validation";
import { LinkedDocuments } from "client/Features/Hub/Management/LinkedDocuments";
import { HubItemImageRestrictionsModel } from "client/Api/Hub/common";
import { DocumentLocationViewModel } from "client/Api/Hub/hubSettings";

const {
	Step,
	ToolbarContainer,
	Content,
	BuilderContainer,
	BuilderLayoutContainer
} = HubItemComposerDialog;

export type ContentStepHandle = {
	getIsContentChanged(): boolean;
};

export type ContentStepProps = {
	isActive: boolean;
	attachable: IObjectInputAttachable<EventFormModel>;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	originalImages: ExistingHubItemImages;
	onChangeIsLoading(isLoading: boolean): void;
	shouldFocusTitle: boolean;
	infoAlerts?: ReactElement;
	isPixabayImageGalleryAvailable: boolean;
};

export const ContentStep = forwardRef(
	(
		props: ContentStepProps,
		forwardedRef: Ref<ContentStepHandle>
	): ReactElement => {
		const {
			isActive,
			attachable,
			imageRestrictions,
			defaultImageLocation,
			originalImages,
			onChangeIsLoading,
			shouldFocusTitle,
			infoAlerts,
			isPixabayImageGalleryAvailable
		} = props;

		const builderRef: RefObject<BuilderHandle | null> = useRef<BuilderHandle>(null);

		useImperativeHandle(
			forwardedRef,
			(): ContentStepHandle => ({
				getIsContentChanged(): boolean {
					return ensureNotNil(builderRef.current)
						.getIsCurrentEditorChanged();
				}
			}),
			[]
		);

		const linkedDocumentsAttachable = attachable.for("linkedDocuments");
		const hasLinkedDocumentsError: boolean = attachable.under("linkedDocuments")
			.validationState
			.kind === InputValidationResultKind.Invalid;

		const hasLinkedDocumentsNameValidationError = useMemo(
			(): boolean => {
				return attachable.for("linkedDocuments")
					.map(
						x => {
							return x.for("name")
								.validation
								.isValid();
						}
					)
					.some(isValid => !isValid);
			},
			[attachable]
		);

		const {
			isSharepointEnabled,
			webDavConfiguration
		} = useMemo(
			(): ({
				isSharepointEnabled: boolean;
				webDavConfiguration: WebDavConfiguration | null;
			}) => {
				const {
					sharePoint,
					webDav
				} = documentsConfiguration();

				return {
					isSharepointEnabled: sharePoint.isConfigured,
					webDavConfiguration: webDav
				};
			},
			[]
		);

		const isAnyStoreEnabled: boolean = isSharepointEnabled
			|| (isNotNil(webDavConfiguration) && webDavConfiguration.isConfigured);

		const disableToolbarHandler = useCallback(
			(): void => {
				builderRef.current
					?.hideToolbar();
			},
			[]
		);

		return (
			<Step
				trackingId="content-step"
				isActive={isActive}
			>
				<Builder
					ref={builderRef}
					imageRestrictions={imageRestrictions}
					defaultImageLocation={defaultImageLocation}
					isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
				>
					<ToolbarContainer isToolbarVisible>
						<Builder.Toolbar />
					</ToolbarContainer>
					<Content hasNoPadding>
						<BuilderContainer>
							{infoAlerts}
							<FormItem
								isRequired
							>
								<BuilderTitleInput
									attachable={attachable.for("title")}
									hubItemKind={HubItemKind.Event}
									shouldFocusTitle={shouldFocusTitle}
									onFocus={disableToolbarHandler}
								/>
							</FormItem>
							<FormItem>
								<HubMediaSelect
									hubItemKind={HubItemKind.Event}
									attachable={attachable.for("media")}
									imageRestrictions={imageRestrictions}
									defaultImageLocation={defaultImageLocation}
									originalImageMedia={originalImages.coverImage}
									isDisabled={false}
									onDisableToolbar={disableToolbarHandler}
									isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
								/>
								<ValidationError {...attachable.for("media").attachToValidationError(true)} />
							</FormItem>
							<FormItem isForGroup>
								<BuilderLayoutContainer>
									<Builder.Layout
										hubItemKind={HubItemKind.Event}
										attachable={attachable.for("layout")}
										originalImages={originalImages.layoutImages}
									/>
								</BuilderLayoutContainer>
								<ValidationError
									{...attachable.for("layout")
										.attachToValidationError()
									}
								/>
							</FormItem>
							{(isAnyStoreEnabled || linkedDocumentsAttachable.value.length > 0) && (
								<>
									<HubItemComposerDialog.SectionTitle
										text={localize("_LinkedDocuments")}
									/>
									<FormItem>
										<LinkedDocuments
											attachable={linkedDocumentsAttachable}
											onChangeIsLoading={onChangeIsLoading}
											isSharepointEnabled={isSharepointEnabled}
											webDavConfiguration={webDavConfiguration}
										/>
										{hasLinkedDocumentsError && (
											<ValidationError
												message={
													hasLinkedDocumentsNameValidationError
														? localize("_SomeLinksDontHaveAName")
														: localize("_SomeLinksAreNotWorkingAnymore")
												}
												trackingId={
													linkedDocumentsAttachable
														.attachToValidationError()
														.trackingId
												}
											/>
										)}
									</FormItem>
								</>
							)}
						</BuilderContainer>
					</Content>
				</Builder>
			</Step>
		);
	}
);

ContentStep.displayName = "ContentStep";